import { IProjectInfo } from 'library/ts/IProjectInfo';
import { ConfigModuleCategory } from 'library/ts/model/enums/config/ConfigModuleCategory';
import { ImportRole } from 'library/ts/model/enums/config/ImportRole';
import { ImportType } from 'library/ts/model/enums/config/ImportType';
import { Protocol } from 'library/ts/model/enums/net/Protocol';
import { Domain } from 'library/ts/model/enums/web/Domain';
import { SubDomain } from 'library/ts/model/enums/web/SubDomain';
import { Namespace } from 'library/ts/namespaces/Namespace';
import { ProjectInfo } from 'library/ts/ProjectInfo';
import { resolveNerdModeBootstrapEnabled } from 'tag/ts/tag/model/config/NerdModeBootstrapResolver';

const projectInfo: IProjectInfo = ProjectInfo;
const urlPrefix = `${Protocol.HTTPS}://${SubDomain.PLAYER_SERVICES}.${Domain.CBSCOM}/`;

/**
 * @description Computed once, at module-evaluation time, from the bootstrap triggers
 * (`?nerdmode=1`/`?nerd=1` on the page URL for Web; the same params, base64-encoded, on the
 * Tag script's own src for CTV) — see `NerdModeBootstrapResolver.ts`. One flag drives both
 * "fetch the plugin script" (`params.import.app.enabled`) and "construct once fetched"
 * (the standard plugin mechanism's category+owner match) — false unless a trigger fired.
 * A host can still override either way via a normal TagConfig merge.
 */
const nerdModeBootstrapEnabled = resolveNerdModeBootstrapEnabled();

/**
 * @copyright © Paramount Streaming
 * @author Stan Gletski
 * @description Nerd Mode diagnostic overlay plugin configuration. Uses the standard plugin
 * loader fields (`params.preload`/`params.owner`/`params.import` — see `TagConfigDataService.ts`
 * for the sibling example) but keeps its own feature settings flat as direct siblings under
 * `params` (matching the pre-plugin-conversion shape) rather than nested under `params.config`
 * — preserves compatibility with existing host overrides of `NerdMode.params.X`.
 */
export const TagConfigNerdMode = {
	category: ConfigModuleCategory.PLUGIN,
	enabled: nerdModeBootstrapEnabled,
	params: {
		preload: true,
		owner: 'PlayerManager',
		import: {
			app: {
				enabled: nerdModeBootstrapEnabled,
				type: ImportType.SCRIPT,
				role: ImportRole.EXECUTABLE,
				namespace: Namespace.TAG_PLUGIN_NERD_MODE,
				url: {
					regular: `${urlPrefix}${projectInfo?.app_output_dir?.tag_plugin_nerd_mode?.token}/${projectInfo?.app_output_file?.tag_plugin_nerd_mode?.js?.min?.token}`
				},
				order: 1
			}
		},
		// Direct polling configuration (no plugin dependencies)
		throttleUpdateInMs: 1000, // Polling interval when visible (1 second for better performance)
		maxLoggedEvents: 50, // Maximum number of player events to track
		colorScheme: null, // Auto-detect from paramsVO.CONTENT.partner
		styles: null, // Use default styles
		header: {
			showKeyboardHint: true, // Show shortcut hint on Web — always off on HTML5/STV embeds regardless of this value, see NerdModeRenderConfigResolver
			showCloseButton: true, // Show close button on Web — always off on HTML5/STV embeds regardless of this value, see NerdModeRenderConfigResolver
			showDetachToggle: true // Show detach/attach toggle on Web — always off on HTML5/STV embeds regardless of this value, see NerdModeRenderConfigResolver
		},
		onStart: null, // Initialization behavior: null = hidden with core metrics (set { show: true, verbose: true } for QA/debug)
		autoScroll: null, // Disabled by default (set { enabled: true, interval: 15000, pauseAtBottom: 3000 } to enable)
		json: null // JSON section defaults: null = each section's own autoExpandDepth/collapseDepth (or built-in 0/1)
	}
};